import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import Select from "react-select";
import { getTaskCodes } from "../api/task-codeAPI";
import { getGroups } from "../api/groupAPI";
import {
  getWorkOrderComponentHistory,
  updateWorkOrder,
} from "../api/work-orderAPI";
import { formatIntDate } from "../utils/dateUtils";
import { useHasPageAccess } from "../masterLayout/MasterLayoutBody";
import ComponentChangeChart from "./child/ComponentChangeChart";
import ComponentLifeHistogram from "./child/ComponentLifeHistogram";

const DEFAULT_START_DATE = "1990-01-01";

const buildTaskCodeLabel = (taskCode) => {
  if (!taskCode) {
    return "Unknown task code";
  }
  const code = taskCode.code ? String(taskCode.code) : "";
  const name = taskCode.name ? String(taskCode.name) : "";
  if (code && name) {
    return `${code} - ${name}`;
  }
  return code || name || `Task Code ${taskCode.id}`;
};

const toNumber = (value) => {
  const numeric = Number(value);
  return Number.isNaN(numeric) ? 0 : numeric;
};

const formatMetric = (value, options = {}) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "—";
  }
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    ...options,
  });
};

const getCostTotals = (costItems = []) => {
  const totals = costItems.reduce(
    (acc, item) => {
      acc.parts += toNumber(item?.partsActualCost);
      acc.labour += toNumber(item?.labourActualCost);
      acc.misc += toNumber(item?.miscActualCost);
      return acc;
    },
    { parts: 0, labour: 0, misc: 0 },
  );
  return { ...totals, total: totals.parts + totals.labour + totals.misc };
};

const getTimeValue = (value) => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  const time = date.getTime();
  return Number.isNaN(time) ? null : time;
};

const ComponentAnalysisLayer = () => {
  const hasPageAccess = useHasPageAccess();
  const [taskCodeOptions, setTaskCodeOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [selectedTaskCode, setSelectedTaskCode] = useState(null);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  const [workOrderRows, setWorkOrderRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [requestError, setRequestError] = useState("");
  const [hasRequested, setHasRequested] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [activeChartTab, setActiveChartTab] = useState("interval");

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [taskCodesResponse, groupsResponse] = await Promise.all([
          getTaskCodes(),
          getGroups(),
        ]);
        const taskCodes = Array.isArray(taskCodesResponse)
          ? taskCodesResponse
          : taskCodesResponse?.data || [];
        const groups = Array.isArray(groupsResponse)
          ? groupsResponse
          : groupsResponse?.data || [];

        setTaskCodeOptions(
          taskCodes.map((taskCode) => ({
            value: taskCode.id,
            label: buildTaskCodeLabel(taskCode),
            data: taskCode,
          })),
        );
        setGroupOptions(
          groups.map((group) => ({
            value: group.id,
            label: group.groupName || `Model ${group.id}`,
            data: group,
          })),
        );
      } catch (error) {
        console.error("Failed to load component analysis filters", error);
      }
    };

    loadFilters();
  }, []);

  const handleShowHistory = useCallback(async () => {
    setFormError("");
    setRequestError("");

    if (!selectedTaskCode?.value) {
      setFormError("Please select a task code before continuing.");
      return;
    }

    if (!selectedGroups.length) {
      setFormError("Please select at least one model before continuing.");
      return;
    }

    if (!startDate || !endDate) {
      setFormError("Please provide both a start and end date.");
      return;
    }

    if (new Date(startDate) > new Date(endDate)) {
      setFormError("Start date must be before the end date.");
      return;
    }

    setLoading(true);
    setHasRequested(true);

    try {
      const response = await getWorkOrderComponentHistory({
        taskCodeIds: [selectedTaskCode.value],
        groupIds: selectedGroups.map((group) => group.value),
        startDate,
        endDate,
        page: 1,
        pageSize: 500,
      });
      const workOrders = Array.isArray(response?.workOrders)
        ? response.workOrders
        : [];
      setWorkOrderRows(
        workOrders.map((workOrder) => ({
          ...workOrder,
          ignoreComponentLife: Number(workOrder?.ignoreComponentLife) === 1,
        })),
      );
    } catch (error) {
      console.error("Failed to fetch component history", error);
      setRequestError("Unable to load component history. Please try again.");
      setWorkOrderRows([]);
    } finally {
      setLoading(false);
    }
  }, [endDate, selectedGroups, selectedTaskCode, startDate]);

  const handleIgnoreComponentLifeToggle = useCallback(
    async (workOrder, nextValue) => {
      if (!workOrder?.id) {
        return;
      }

      const previousValue = Boolean(workOrder.ignoreComponentLife);
      if (nextValue === previousValue) {
        return;
      }

      setUpdatingId(workOrder.id);
      setWorkOrderRows((prevRows) =>
        prevRows.map((row) =>
          row.id === workOrder.id ? { ...row, ignoreComponentLife: nextValue } : row,
        ),
      );

      try {
        await updateWorkOrder(workOrder.id, { ignoreComponentLife: nextValue ? 1 : 0 });
      } catch (error) {
        console.error("Failed to update ignoreComponentLife for work order", error);
        setWorkOrderRows((prevRows) =>
          prevRows.map((row) =>
            row.id === workOrder.id
              ? { ...row, ignoreComponentLife: previousValue }
              : row,
          ),
        );
      } finally {
        setUpdatingId(null);
      }
    },
    [],
  );

  const averagedRows = useMemo(
    () => workOrderRows.filter((row) => !row.ignoreComponentLife),
    [workOrderRows],
  );

  const averageMetrics = useMemo(() => {
    if (!averagedRows.length) {
      return null;
    }

    const totals = averagedRows.reduce(
      (acc, workOrder) => {
        const costTotals = getCostTotals(workOrder.costItems || []);
        acc.parts += costTotals.parts;
        acc.labour += costTotals.labour;
        acc.misc += costTotals.misc;
        acc.total += costTotals.total;
        acc.labourHours += toNumber(workOrder.labourActualHours);
        acc.componentLife += toNumber(workOrder.componentLife);
        return acc;
      },
      { parts: 0, labour: 0, misc: 0, total: 0, labourHours: 0, componentLife: 0 },
    );

    const divisor = averagedRows.length || 1;
    return {
      parts: totals.parts / divisor,
      labour: totals.labour / divisor,
      misc: totals.misc / divisor,
      total: totals.total / divisor,
      labourHours: totals.labourHours / divisor,
      componentLife: totals.componentLife / divisor,
    };
  }, [averagedRows]);

  const chartWorkOrders = useMemo(
    () => workOrderRows.filter((workOrder) => !workOrder?.ignoreComponentLife),
    [workOrderRows],
  );

  const componentChangeChart = useMemo(() => {
    if (!chartWorkOrders.length) {
      return [];
    }

    const countsByDate = chartWorkOrders.reduce((acc, workOrder) => {
      const completedTime = getTimeValue(workOrder?.completedAt);
      if (completedTime == null) {
        return acc;
      }
      const dateKey = new Date(completedTime).toISOString().slice(0, 10);
      acc[dateKey] = (acc[dateKey] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(countsByDate)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [chartWorkOrders]);

  const achievedLives = useMemo(
    () =>
      chartWorkOrders
        .map((workOrder) => {
          const value = parseInt(workOrder?.componentLife, 10);
          const numericValue = value != null ? Number(value) : null;
          return Number.isFinite(numericValue) ? numericValue : null;
        })
        .filter((value) => value != null),
    [chartWorkOrders],
  );

  const expectedLife = useMemo(() => {
    if (!achievedLives.length) {
      return null;
    }
    const total = achievedLives.reduce((sum, value) => sum + value, 0);
    return total / achievedLives.length;
  }, [achievedLives]);

  const bucketSize = useMemo(() => {
    if (!achievedLives.length) {
      return 1;
    }
    const maxLife = Math.max(...achievedLives);
    if (maxLife < 2000) {
      return 50;
    }
    if (maxLife > 50000 && maxLife < 100000) {
      return 5000;
    }
    return 1000;
  }, [achievedLives]);

  if (!hasPageAccess("/component-analysis")) {
    return <Navigate to="/access-denied" replace />;
  }

  return (
    <div className="card h-100 p-0 rounded-0">
      <div className="card-body p-24">
        <div className="row g-3">
          <div className="col-xl-4 col-md-6">
            <label className="form-label">Task Code</label>
            <Select
              classNamePrefix="react-select"
              options={taskCodeOptions}
              value={selectedTaskCode}
              onChange={(option) => setSelectedTaskCode(option)}
              placeholder="Select a task code"
              isClearable
            />
          </div>
          <div className="col-xl-4 col-md-6">
            <label className="form-label">Model</label>
            <Select
              classNamePrefix="react-select"
              options={groupOptions}
              value={selectedGroups}
              onChange={(options) => setSelectedGroups(options || [])}
              placeholder="Select one or more models"
              isMulti
            />
          </div>
          <div className="col-xl-2 col-md-6">
            <label className="form-label">Start Date</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div className="col-xl-2 col-md-6">
            <label className="form-label">End Date</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
        <div className="d-flex justify-content-end mt-24 mb-16">
          <button
            type="button"
            className="btn btn-primary radius-4"
            onClick={handleShowHistory}
            disabled={loading}
          >
            {loading ? "Loading..." : "Load History"}
          </button>
        </div>
        <div className="d-flex justify-content-end">
          {formError ? <div className="text-danger">{formError}</div> : null}
          {requestError ? <div className="text-danger">{requestError}</div> : null}
        </div>
        {!hasRequested ? (
          <div className="text-muted">
            Select filters and press &ldquo;Load History&rdquo; to view results.
          </div>
        ) : (
          <div className="mt-24">
            <div
              className="table-responsive"
              style={{ maxHeight: "40vh", overflowY: "auto" }}
            >
              <table className="table bordered-table mb-0 w-100 rounded-0">
                <thead>
                  <tr>
                    <th>Work Order Number</th>
                    <th>Work Order Title</th>
                    <th>Asset</th>
                    <th>Completed</th>
                    <th>Task Type</th>
                    <th>Interval in Units</th>
                    <th>Parts Cost</th>
                    <th>Labour Cost</th>
                    <th>Services Cost</th>
                    <th>Total Cost</th>
                    <th>Labour Hours</th>
                    <th>Ignore</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="text-center py-4">
                        Loading work orders...
                      </td>
                    </tr>
                  ) : workOrderRows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="text-center py-4">
                        No Work Orders Found
                      </td>
                    </tr>
                  ) : (
                    workOrderRows.map((workOrder) => {
                      const costTotals = getCostTotals(workOrder.costItems || []);
                      return (
                        <tr key={workOrder.id || workOrder.workOrderNumber}>
                          <td>{workOrder.workOrderNumber || ""}</td>
                          <td>{workOrder.workOrderTitle || ""}</td>
                          <td>{workOrder.assetNumber}</td>
                          <td>{formatIntDate(workOrder.completedAt) || ""}</td>
                          <td>{workOrder?.taskType?.taskTypeCode || ""}</td>
                          <td className="text-end">{formatMetric(workOrder.componentLife)}</td>
                          <td className="text-end">{formatMetric(costTotals.parts)}</td>
                          <td className="text-end">{formatMetric(costTotals.labour)}</td>
                          <td className="text-end">{formatMetric(costTotals.misc)}</td>
                          <td className="text-end">{formatMetric(costTotals.total)}</td>
                          <td className="text-end">{formatMetric(workOrder.labourActualHours)}</td>
                          <td>
                            <div className="form-switch switch-primary d-flex align-items-center gap-3 mb-0">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id={`ignore-component-life-${workOrder.id}`}
                                checked={Boolean(workOrder.ignoreComponentLife)}
                                onChange={(event) =>
                                  handleIgnoreComponentLifeToggle(
                                    workOrder,
                                    event.target.checked,
                                  )
                                }
                                disabled={updatingId === workOrder.id}
                                aria-label="Toggle ignore component life"
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {workOrderRows.length > 0 && !loading ? (
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="fw-semibold">
                        Average (excluding ignored)
                      </td>
                      <td className="fw-semibold text-end">
                        {formatMetric(averageMetrics?.componentLife)}
                      </td>
                      <td className="fw-semibold text-end">{formatMetric(averageMetrics?.parts)}</td>
                      <td className="fw-semibold text-end">{formatMetric(averageMetrics?.labour)}</td>
                      <td className="fw-semibold text-end">{formatMetric(averageMetrics?.misc)}</td>
                      <td className="fw-semibold text-end">{formatMetric(averageMetrics?.total)}</td>
                      <td className="fw-semibold text-end">{formatMetric(averageMetrics?.labourHours)}</td>
                      <td />
                    </tr>
                  </tfoot>
                ) : null}
              </table>
            </div>
            <div className="mt-24">
              <ul className="nav nav-tabs mt-8">
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activeChartTab === "interval" ? "active" : ""}`}
                    onClick={() => setActiveChartTab("interval")}
                  >
                    Interval
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link ${activeChartTab === "timeline" ? "active" : ""}`}
                    onClick={() => setActiveChartTab("timeline")}
                  >
                    Timeline
                  </button>
                </li>
              </ul>
              <div className="mt-4">
                {activeChartTab === "interval" ? (
                  <ComponentLifeHistogram
                    achievedLives={achievedLives}
                    expectedLife={expectedLife}
                    bucketSize={bucketSize}
                  />
                ) : (
                  <ComponentChangeChart changeData={componentChangeChart} />
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComponentAnalysisLayer;
